/* Zaroori Jini OMS — Book a Demo page.
 * A lightweight scheduling form that gathers preferred slot + product interests
 * and dispatches a mailto draft.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Calendar, Clock, Mail, User, Building2, ShoppingCart, CheckCircle2, ArrowRight } from "lucide-react";
import {
  RED, INK, MUTED, CREAM, BORDER,
  Chip, Section, PrimaryButton, FieldLabel, FieldInput, MarketingShell,
} from "@/components/marketing/atoms";

const INTERESTS = [
  "AI Order Management", "Warehouse Management", "Marketplace Integrations",
  "Shipping & Couriers", "Returns Management", "Finance & Reconciliation",
  "Automation Engine", "AI Command Center", "Multi-Tenant / Agency",
];

const SLOTS = ["Morning (10am-1pm)", "Afternoon (1pm-4pm)", "Evening (4pm-7pm)"];

export default function Demo() {
  const [form, setForm] = useState({
    name: "", email: "", company: "",
    date: "", slot: SLOTS[0], marketplaces: "",
  });
  const [interests, setInterests] = useState([]);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (v) => setInterests((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.date) {
      return toast.error("Please fill name, email, company, and preferred date.");
    }
    try {
      const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const payload = {
        name: form.name,
        email: form.email,
        company: form.company,
        marketplaces: form.marketplaces,
        preferredSlot: `${form.date} · ${form.slot}`,
        message: interests.length ? `Interested in: ${interests.join(", ")}` : undefined,
      };
      const r = await fetch(`${backend}/api/leads/demo-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(await r.text());
      setDone(true);
      toast.success("Demo request received — check your inbox. We'll confirm a slot within 4 hours.");
    } catch (err) {
      toast.error("Something went wrong — please email us directly at hello@zarooriinfotech.in");
    }
  };

  return (
    <MarketingShell testid="demo-page">
      <section
        style={{
          background: `linear-gradient(180deg, ${CREAM} 0%, #fff 100%)`,
          padding: "64px 24px 32px", borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <Chip>Book a Live Demo</Chip>
          <h1
            style={{
              fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 800, color: INK,
              marginTop: 18, letterSpacing: "-0.03em", lineHeight: 1.05,
            }}
          >
            See Zaroori Jini in Action
          </h1>
          <p
            style={{
              color: MUTED, fontSize: 16, lineHeight: 1.65,
              maxWidth: 640, margin: "20px auto 0",
            }}
          >
            45-minute walkthrough tailored to your marketplaces, warehouses, and workflows.
            Bring your team — we&apos;ll answer every question.
          </p>
        </div>
      </section>

      <Section style={{ background: "#fff" }}>
        <div
          className="demo-grid"
          style={{ display: "grid", gridTemplateColumns: "1.2fr 0.9fr", gap: 40, alignItems: "start" }}
        >
          <form
            onSubmit={submit}
            data-testid="demo-form"
            style={{
              background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16,
              padding: 28, boxShadow: "0 20px 50px -30px rgba(15,23,42,0.15)",
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }} className="demo-form-grid">
              <div>
                <FieldLabel>Name *</FieldLabel>
                <FieldInput icon={<User size={14} color={MUTED} />} value={form.name} onChange={upd("name")} placeholder="Your name" testid="demo-name" required />
              </div>
              <div>
                <FieldLabel>Work Email *</FieldLabel>
                <FieldInput icon={<Mail size={14} color={MUTED} />} type="email" value={form.email} onChange={upd("email")} placeholder="you@brand.com" testid="demo-email" required />
              </div>
              <div>
                <FieldLabel>Company *</FieldLabel>
                <FieldInput icon={<Building2 size={14} color={MUTED} />} value={form.company} onChange={upd("company")} placeholder="Brand / Company" testid="demo-company" required />
              </div>
              <div>
                <FieldLabel>Marketplaces</FieldLabel>
                <FieldInput icon={<ShoppingCart size={14} color={MUTED} />} value={form.marketplaces} onChange={upd("marketplaces")} placeholder="Amazon, Flipkart, Myntra…" testid="demo-marketplaces" />
              </div>
              <div>
                <FieldLabel>Preferred Date *</FieldLabel>
                <FieldInput icon={<Calendar size={14} color={MUTED} />} type="date" value={form.date} onChange={upd("date")} testid="demo-date" required />
              </div>
              <div>
                <FieldLabel>Preferred Time Slot</FieldLabel>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="demo-slot-picker">
                  {SLOTS.map((s) => {
                    const a = form.slot === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, slot: s }))}
                        style={{
                          padding: "8px 12px",
                          border: `1px solid ${a ? RED : BORDER}`,
                          background: a ? "#FFF5F5" : "#fff",
                          color: a ? RED : INK,
                          borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer",
                        }}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 18 }}>
              <FieldLabel>What are you most interested in?</FieldLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="demo-interest-picker">
                {INTERESTS.map((it) => {
                  const active = interests.includes(it);
                  return (
                    <button
                      key={it}
                      type="button"
                      onClick={() => toggle(it)}
                      style={{
                        padding: "8px 12px",
                        border: `1px solid ${active ? RED : BORDER}`,
                        background: active ? "#FFF5F5" : "#fff",
                        color: active ? RED : INK,
                        borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
                      }}
                    >
                      {it}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ marginTop: 24 }}>
              <PrimaryButton type="submit" testid="demo-submit">
                Request Demo <ArrowRight size={16} />
              </PrimaryButton>
            </div>

            {done && (
              <div
                data-testid="demo-success"
                style={{
                  marginTop: 20, padding: "14px 16px", background: "#ECFDF5",
                  border: "1px solid #A7F3D0", color: "#065F46", borderRadius: 8,
                  display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13,
                }}
              >
                <CheckCircle2 size={18} />
                <div>
                  Your demo request has been drafted. If your email client didn&apos;t open,
                  write to <b>demo@zaroorijini.com</b>.
                </div>
              </div>
            )}
          </form>

          <div
            style={{
              background: "#0F172A", color: "#fff", borderRadius: 16,
              padding: 26,
            }}
          >
            <div style={{ fontSize: 12, color: "#F87171", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase" }}>
              What you&apos;ll see
            </div>
            <ul style={{ padding: 0, listStyle: "none", marginTop: 14 }}>
              {[
                "Live tour of the AI Command Center",
                "Marketplace connectors — configured live",
                "Warehouse operations & barcode flow",
                "AI Fulfillment & courier auto-selection",
                "Automation Engine — real triggers, real rules",
                "Migration plan tailored to your stack",
              ].map((t) => (
                <li
                  key={t}
                  style={{
                    padding: "10px 0", display: "flex", gap: 10, alignItems: "center",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    color: "#CBD5E1", fontSize: 13,
                  }}
                >
                  <CheckCircle2 size={14} color="#F87171" /> {t}
                </li>
              ))}
            </ul>
            <div
              style={{
                marginTop: 20, padding: "10px 14px", background: "rgba(220,38,38,0.15)",
                borderRadius: 10, fontSize: 12, color: "#FCA5A5", fontWeight: 700,
                display: "flex", alignItems: "center", gap: 8,
              }}
            >
              <Clock size={14} /> Typical response: within 4 business hours
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 960px) { .demo-grid { grid-template-columns: 1fr !important; } }
          @media (max-width: 640px) { .demo-form-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </Section>
    </MarketingShell>
  );
}
